/**
 * Arlo & Co — Product Options
 * Reads window.ArloProductConfig.productOptions[handle] and injects
 * dropdown / buttons / tiles / checkbox fields into the product form,
 * submitted as Shopify line item properties (properties[Label]).
 *
 * Requires: arlo-product-config.js loaded first.
 */

(function () {
  'use strict';

  // Guard against double-execution: arlo-options.js is loaded by both
  // canvas-capture.liquid and uploadcare-fields.liquid. The second load must
  // be a no-op — otherwise buildFields() resets fpFontHidden to the default
  // font, wiping the user's font selection before cart submit.
  if (window._arloOptionsLoaded) return;
  window._arloOptionsLoaded = true;

  // Mark this page as Arlo-managed the instant this script is parsed.
  // Runs synchronously so CSS rules scoped to html.arlo-managed apply
  // before the browser paints — no flash of old option blocks.
  document.documentElement.classList.add('arlo-managed');

  // Badge label for an option button value: percentage text as authored, or a
  // computed "Save $X" when bv.badgeStyle === 'dollar'. Dollar savings round down
  // to the nearest 50c to match the page-price badge.
  function arloOptBadgeLabel(bv, baseCents) {
    if (!bv.discountBadge) return '';
    // Dollar is the default; only show the literal percentage when explicitly chosen.
    if (bv.badgeStyle === 'percent') return bv.discountBadge;
    var m = bv.discountBadge.match(/(\d+(?:\.\d+)?)/);
    var pct = m ? parseFloat(m[1]) : null;
    var savings = null;
    if (bv.compareAtPrice && bv.salePrice) {
      savings = Math.round((parseFloat(bv.compareAtPrice) - parseFloat(bv.salePrice)) * 100);
    } else if (pct != null) {
      // Pre-discount total this button represents:
      //   quantity buttons → base product price × parentQty
      //   explicit price   → compareAt or price
      var fullCents = (bv.parentQty && baseCents) ? baseCents * bv.parentQty
                    : bv.compareAtPrice ? Math.round(parseFloat(bv.compareAtPrice) * 100)
                    : bv.price ? Math.round(parseFloat(bv.price) * 100)
                    : (baseCents || null);
      if (fullCents != null) savings = Math.round(fullCents * pct / 100);
    }
    if (savings != null && savings > 0) {
      var dollars = (Math.floor(savings / 50) * 50) / 100;
      return 'Save $' + (dollars % 1 === 0 ? dollars : dollars.toFixed(2));
    }
    return bv.discountBadge;
  }

  // ── Step coordinator ──────────────────────────────────────────────────────────
  // Shared global across all three Arlo scripts (options / variant-buttons / photo-uploader).
  // Each script registers its badge <span> elements here. After ALL DOMContentLoaded
  // handlers have fired (setTimeout 0), badges are sorted by DOM position and numbered
  // 1, 2, 3... in visual order. No fragile config-handle lookups or DOM timing hacks.
  if (!window._arloStepCoord) {
    window._arloStepCoord = {
      _badges: [],
      _scheduled: false,
      register: function (el) {
        this._badges.push(el);
        if (!this._scheduled) {
          this._scheduled = true;
          var self = this;
          setTimeout(function () {
            self._badges.sort(function (a, b) {
              // Node.DOCUMENT_POSITION_FOLLOWING === 4
              return (a.compareDocumentPosition(b) & 4) ? -1 : 1;
            });
            // Only show step numbers when there are 2+ steps
            if (self._badges.length > 1) {
              self._badges.forEach(function (b, i) { b.textContent = i + 1; });
            } else {
              self._badges.forEach(function (b) { b.style.display = 'none'; });
            }
          }, 0);
        }
      }
    };
  }

  var CONTAINER_ID = 'arlo-options-container';

  // ── Switch product image (replicates Artisan theme's Ca() function) ───────────
  function arloSwitchProductImage(imageIndex) {
    // console.log('[ArloOptions] arloSwitchProductImage called with index:', imageIndex);
    var photoContainers = document.querySelectorAll(
      '.product__media-container.above-mobile, .product__media-container.below-mobile'
    );
    // console.log('[ArloOptions] photoContainers found:', photoContainers.length);
    photoContainers.forEach(function (container) {
      var wrappers = container.querySelectorAll('[data-product-media-wrapper]');
      wrappers.forEach(function (w, i) {
        if (i === imageIndex) {
          w.classList.remove('hidden');
        } else {
          w.classList.add('hidden');
        }
      });
    });

    // Update active state on all thumbnail buttons
    document.querySelectorAll('[data-product-thumbnail]').forEach(function (t) {
      var isActive = String(t.dataset.thumbnailIndex) === String(imageIndex);
      t.classList.toggle('active', isActive);
    });

    // Tell the Artisan thumbnail strip to scroll to this slide
    var scrollerEl = document.querySelector('[data-scroller-id]');
    var scrollerId = scrollerEl && scrollerEl.dataset.scrollerId;
    if (scrollerId) {
      document.dispatchEvent(new CustomEvent(
        'scroll-slider-' + scrollerId + ':go-to-slide',
        { detail: { slideIndex: String(imageIndex) } }
      ));
    }
  }

  // ── Get options for current product ──────────────────────────────────────────
  function getOptions() {
    var config = window.ArloProductConfig;
    if (!config || !config.productOptions) return null;
    var handle = (window.ShopifyAnalytics &&
                  window.ShopifyAnalytics.meta &&
                  window.ShopifyAnalytics.meta.product &&
                  window.ShopifyAnalytics.meta.product.handle) || null;
    if (!handle) {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      handle = m ? m[1] : null;
    }
    if (!handle) return null;
    return config.productOptions[handle] || null;
  }

  // ── Linked product price tracking ─────────────────────────────────────────────
  var _linkedExtraCents = 0;
  var _buttonBasePriceCents = null;  // captured once before first button-option price override
  var _buttonPriceActive = false;    // true when a button option is overriding the page price

  // ── Live variant price cache ───────────────────────────────────────────────────
  // Fetches current prices from Shopify storefront so stale config prices are ignored.
  var _liveVariantPrices = {};  // variantId (string) → price in dollars (number) or null

  function prefetchLinkedVariantPrices(opts) {
    var ids = [];
    opts.forEach(function (opt) {
      if (opt.linkedVariantId) ids.push(String(opt.linkedVariantId));
      if (opt.buttonValues) {
        opt.buttonValues.forEach(function (bv) {
          if (bv.linkedVariantId) ids.push(String(bv.linkedVariantId));
        });
      }
    });
    // Deduplicate and fetch
    ids.filter(function (id, i, a) { return a.indexOf(id) === i; }).forEach(function (id) {
      if (_liveVariantPrices[id] !== undefined) return;
      _liveVariantPrices[id] = null; // mark as pending so we don't double-fetch
      fetch('/variants/' + id + '.json')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          _liveVariantPrices[id] = data.variant ? parseFloat(data.variant.price) : null;
        })
        .catch(function () { _liveVariantPrices[id] = null; });
    });
  }

  function getLiveLinkedPrice(variantId) {
    // Returns live price in dollars, or null if not yet fetched / not available
    return _liveVariantPrices[String(variantId)] !== undefined
      ? _liveVariantPrices[String(variantId)]
      : null;
  }

  function formatMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    return fmt.replace('{{amount}}', amount)
              .replace('{{amount_no_decimals}}', Math.round(cents / 100))
              .replace('{{amount_with_comma_separator}}', amount)
              .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(cents / 100));
  }

  function applyLinkedPriceDelta(deltaCents) {
    _linkedExtraCents += deltaCents;
    document.querySelectorAll('[data-price]').forEach(function (el) {
      if (!el.dataset.arloBaseCents) {
        // Parse the current displayed price to get base cents (first time only)
        var raw = el.textContent.replace(/[^0-9.]/g, '');
        el.dataset.arloBaseCents = Math.round(parseFloat(raw) * 100) || 0;
      }
      var base = parseInt(el.dataset.arloBaseCents, 10);
      el.innerHTML = formatMoney(base + _linkedExtraCents);
    });
  }

  // ── Button-option page price (direct SET, not delta) ─────────────────────────
  var _PRICE_SELECTORS = [
    '[data-product-price]',
    '.price__current .money',
    '.price-item--regular',
    '.product__price .money',
    '.product-single__price',
    '[data-price]',
  ].join(',');

  function _captureBasePrice() {
    if (_buttonBasePriceCents !== null) return;
    var el = document.querySelector(_PRICE_SELECTORS);
    if (el) {
      var raw = el.textContent.replace(/[^0-9.]/g, '');
      _buttonBasePriceCents = Math.round(parseFloat(raw) * 100) || 0;
    }
  }

  function setOptButtonPagePrice(bv) {
    // NB: bv.hidePrice hides the price on the button CARD only (handled where the
    // card price row is built). It must NOT stop the main page price from updating —
    // a button can hide its own price yet still change the product's displayed price.
    _captureBasePrice();

    var priceCents, compareAtCents = 0;

    if (bv.price || bv.salePrice) {
      // ── Explicit price mode: admin-entered price overrides everything ──────────
      priceCents = Math.round(parseFloat(bv.salePrice || bv.price) * 100);
      if (bv.compareAtPrice) compareAtCents = Math.round(parseFloat(bv.compareAtPrice) * 100);
    } else if (bv.parentQty && _buttonBasePriceCents) {
      // ── Auto mode: base price × qty, then apply discount badge ────────────────
      var multipliedCents = _buttonBasePriceCents * bv.parentQty;
      if (bv.discountBadge) {
        var pctMatch = bv.discountBadge.match(/\d+/);
        if (pctMatch) {
          var pct = parseInt(pctMatch[0]) / 100;
          // Round to nearest $0.05 for clean prices
          priceCents = Math.round(multipliedCents * (1 - pct) / 5) * 5;
          compareAtCents = multipliedCents;
        } else {
          priceCents = multipliedCents;
        }
      } else {
        priceCents = multipliedCents;
      }
    } else if (bv.addonPrice && _buttonBasePriceCents !== null) {
      // ── Add-on mode: base price + linked product's price ──────────────────────
      priceCents = _buttonBasePriceCents + Math.round(parseFloat(bv.addonPrice) * 100);
      compareAtCents = 0;
    } else {
      // No price config — leave page price alone
      restoreOptButtonPagePrice();
      return;
    }

    _buttonPriceActive = true;

    // Shopify moneyFormat may contain HTML (e.g. <span class=money>$X</span>)
    // Strip tags to get plain text so we can safely set textContent
    function _plainMoney(cents) {
      return formatMoney(cents).replace(/<[^>]+>/g, '');
    }

    var plainPrice = _plainMoney(priceCents);
    document.querySelectorAll(_PRICE_SELECTORS).forEach(function (el) {
      if (/[\$£€¥]/.test(el.textContent) || el.hasAttribute('data-product-price') || el.hasAttribute('data-price')) {
        // If the element has an inner .money span, update that; otherwise the element itself
        var moneyEl = el.querySelector('.money') || el;
        moneyEl.textContent = plainPrice;
      }
    });
    // Compare-at price
    if (compareAtCents) {
      var plainCompare = _plainMoney(compareAtCents);
      document.querySelectorAll('.price__compare .money, [data-compare-at-price], .price__was .money, .price-item--sale').forEach(function (el) {
        el.textContent = plainCompare;
        var wrap = el.closest('[style*="display: none"]') || el.closest('.price__compare');
        if (wrap && wrap.style.display === 'none') wrap.style.display = '';
      });
    }
    // Discount badge — inject once, reuse; matches .arlo-vb-badge style exactly
    var badge = document.getElementById('arlo-btn-opt-badge') || (function () {
      var b = document.createElement('span');
      b.id = 'arlo-btn-opt-badge';
      b.className = 'arlo-vb-badge';
      b.style.cssText = 'display:inline-block;margin-top:6px';
      var priceEl = document.querySelector('[data-product-price],.price__current,.product__price');
      if (priceEl) priceEl.appendChild(b);
      return b;
    })();
    if (bv.discountBadge) {
      // Show dollar saving if we have both prices; otherwise fall back to badge text
      var savingsCents = compareAtCents - priceCents;
      var savingsDisplay = (function() {
        var rounded = Math.floor(savingsCents / 50) * 50; // round DOWN to nearest 50c
        var dollars = rounded / 100;
        return '$' + (dollars % 1 === 0 ? dollars : dollars.toFixed(2));
      })();
      badge.textContent = (compareAtCents && savingsCents > 0)
        ? 'Save ' + savingsDisplay
        : bv.discountBadge;
      badge.style.display = 'inline-block';
    } else {
      badge.style.display = 'none';
    }
  }

  function restoreOptButtonPagePrice() {
    if (!_buttonPriceActive) return;
    _buttonPriceActive = false;
    if (_buttonBasePriceCents !== null) {
      var plainPrice = formatMoney(_buttonBasePriceCents + _linkedExtraCents).replace(/<[^>]+>/g, '');
      document.querySelectorAll(_PRICE_SELECTORS).forEach(function (el) {
        if (/[\$£€¥]/.test(el.textContent) || el.hasAttribute('data-product-price') || el.hasAttribute('data-price')) {
          var moneyEl = el.querySelector('.money') || el;
          moneyEl.textContent = plainPrice;
        }
      });
    }
    // Hide the injected badge
    var badge = document.getElementById('arlo-btn-opt-badge');
    if (badge) badge.style.display = 'none';
  }

  // ── Product image switcher ────────────────────────────────────────────────────
  function setProductImageByIndex(oneBased) {
    var idx = (oneBased || 1) - 1; // convert to 0-based
    // Try Artisan/Dawn media gallery thumbnails
    var thumbs = document.querySelectorAll(
      '.product-gallery__thumbnails li, .product__media-list .product__media-item, ' +
      '.thumbnails-slider__item, [data-media-type] .thumbnail, ' +
      '.product-single__thumbnails .product-single__thumbnail-item'
    );
    if (thumbs.length > idx) {
      var t = thumbs[idx];
      var link = t.querySelector('a, button') || t;
      link.click();
      return;
    }
    // Fallback: look for a Swiper/Slick slide and simulate swipe
    var slides = document.querySelectorAll(
      '.product-gallery__slide, .product__media-item, .slick-slide[data-slick-index], .swiper-slide'
    );
    if (slides.length > idx) {
      slides[idx].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }
  }

  // Reset base price cache whenever the theme swaps variant (so our delta applies to fresh base)
  document.addEventListener('product:variant-change', function (e) {
    var variant = e.detail && e.detail.variant;
    if (!variant) return;
    // Reset button-option base price so next override captures the new variant price
    _buttonBasePriceCents = variant.price;
    _buttonPriceActive = false;
    document.querySelectorAll('[data-price]').forEach(function (el) {
      el.dataset.arloBaseCents = variant.price;
      el.innerHTML = formatMoney(variant.price + _linkedExtraCents);
    });
  });

  // ── Inject styles ─────────────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('arlo-options-styles')) return;
    var s = document.createElement('style');
    s.id = 'arlo-options-styles';
    s.textContent = `
      #arlo-options-container { margin-bottom: 20px; }

      /* Each option block */
      .arlo-opt { margin-bottom: 16px; }

      /* Shared label above every option type */
      .arlo-opt-label {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        margin-bottom: 8px;
        color: var(--color-foreground, #111);
        font-size: 12px; letter-spacing: 0.08em;
      }
      .arlo-opt-step-badge {
        width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0;
        background: var(--color-button-primary-background, #111);
        color: var(--color-button-primary-text, #fff);
        font-size: 13px; font-weight: 700;
        display: flex; align-items: center; justify-content: center;
      }
      .arlo-opt-required { color: var(--color-error, #c0392b); margin-left: 2px; }

      /* ── Dropdown ── */
      .arlo-opt-select {
        width: 100%;
        padding: 10px 14px;
        font-size: 14px;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        appearance: none;
        -webkit-appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23666' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 14px center;
        cursor: pointer;
        font-family: inherit;
      }
      .arlo-opt-select:focus { outline: 2px solid var(--color-foreground, #111); outline-offset: 2px; }
      .arlo-opt-select.arlo-opt-error { border-color: var(--color-error, #c0392b); }

      /* ── Buttons — horizontal scroll row (few items fill, many scroll) ── */
      .arlo-opt-buttons {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 10px;
        align-items: stretch;            /* all cards match the tallest — no jumping */
        scroll-snap-type: x proximity;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 6px;             /* room for the scrollbar */
      }
      .arlo-opt-buttons > .arlo-vb-item {
        flex: 1 0 140px;                 /* grow to fill when few; min 140px then scroll */
        scroll-snap-align: start;
      }
      .arlo-opt-buttons::-webkit-scrollbar { height: 6px; }
      .arlo-opt-buttons::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }

      /* .arlo-vb-btn from arlo-variant-buttons.js provides the card base.
         Only override active + error states here. */
      .arlo-opt-buttons .arlo-vb-title { text-align: center; }
      .arlo-opt-buttons .arlo-vb-desc { text-align: center; font-size: 10px; color: var(--color-foreground-secondary, #666); line-height: 1.3; margin-top: 1px; }
      .arlo-opt-button.active {
        box-shadow: inset 0 0 0 2.5px var(--color-button-primary-background, #111) !important;
        background-color: color-mix(in srgb, var(--color-button-primary-background, #111) 15%, transparent) !important;
      }
      @supports not (color: color-mix(in srgb, red 15%, transparent)) {
        .arlo-opt-button.active { background-color: rgba(0,0,0,0.07) !important; }
      }
      .arlo-opt-buttons.arlo-opt-error .arlo-opt-button:not(.active) {
        box-shadow: inset 0 0 0 1.5px var(--color-error, #c0392b) !important;
      }

      /* ── Tiles — horizontal scroll row (few items fill, many scroll) ── */
      .arlo-opt-tiles {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        gap: 10px;
        align-items: stretch;            /* all tiles match the tallest — no jumping */
        scroll-snap-type: x proximity;
        -webkit-overflow-scrolling: touch;
        padding-bottom: 6px;
      }
      .arlo-opt-tiles::-webkit-scrollbar { height: 6px; }
      .arlo-opt-tiles::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }

      .arlo-opt-tile {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        padding: 6px 6px 8px;
        gap: 6px;
        border: 1.5px solid var(--color-border, #d1d1d1);
        border-radius: var(--radius, 6px);
        background: var(--color-background, #fff);
        cursor: pointer;
        font-family: inherit;
        transition: border-color 0.15s, box-shadow 0.15s;
        box-sizing: border-box;
        flex: 1 0 104px;                 /* grow to fill when few; min 104px then scroll */
        scroll-snap-align: start;
      }
      .arlo-opt-tile:hover { border-color: var(--color-foreground, #111); }
      .arlo-opt-tile.active {
        border-color: var(--color-foreground, #111);
        border-width: 2px;
        box-shadow: 0 0 0 1px var(--color-foreground, #111);
      }
      .arlo-opt-tile-swatch {
        width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 3px;
        background-size: cover;
        background-position: center;
      }
      .arlo-opt-tile-label {
        font-size: 13px;
        font-weight: 600;
        color: var(--color-foreground-secondary, #666);
        text-align: center;
        width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        line-height: 1.2;
      }
      .arlo-opt-tiles.arlo-opt-error .arlo-opt-tile {
        border-color: var(--color-error, #c0392b);
      }

      /* ── Checkbox ── */
      .arlo-opt-checkbox-wrap {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        cursor: pointer;
        padding: 10px 14px;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
      }
      .arlo-opt-checkbox-wrap.arlo-opt-error { border-color: var(--color-error, #c0392b); }
      .arlo-opt-checkbox {
        width: 18px; height: 18px;
        accent-color: var(--color-foreground, #111);
        cursor: pointer;
        flex-shrink: 0;
      }
      .arlo-opt-checkbox-label { font-size: 14px; color: var(--color-foreground, #111); }

      /* ── Monogram input ── */
      .arlo-opt-mono-row {
        display: flex;
        gap: 8px;
        align-items: stretch;
        position: relative;
      }
      .arlo-opt-mono-required {
        position: absolute;
        bottom: 5px;
        left: 6px;
        font-size: 11px;
        font-weight: 600;
        color: var(--color-error, #c0392b);
        line-height: 1;
        pointer-events: none;
      }
      .arlo-opt-mono-input {
        flex: 1;
        padding: 10px 14px;
        font-size: 22px;
        font-family: 'Times New Roman', Times, serif;
        letter-spacing: 0.2em;
        text-indent: 0.2em; /* compensates for trailing letter-spacing so text stays visually centred */
        text-transform: uppercase;
        text-align: center;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        font-variant-emoji: text; /* force ♥ to render as black text, not red emoji */
        min-width: 0;
      }
      .arlo-opt-mono-input:focus { outline: 2px solid var(--color-foreground, #111); outline-offset: 2px; }
      .arlo-opt-mono-input.arlo-opt-error { border-color: var(--color-error, #c0392b); }
      .arlo-opt-mono-heart {
        padding: 0 14px;
        font-size: 22px; /* matches input font-size so ♥ is same height as letters */
        font-family: 'Times New Roman', Times, serif;
        font-variant-emoji: text;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        cursor: pointer;
        flex-shrink: 0;
        line-height: 1;
      }
      .arlo-opt-mono-heart:hover { border-color: var(--color-foreground, #111); }
      .arlo-opt-mono-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        margin-top: 6px;
      }
      .arlo-opt-mono-hint {
        font-size: 11px;
        color: var(--color-foreground-secondary, #666);
        white-space: nowrap;
      }
      .arlo-opt-mono-pips {
        display: flex;
        gap: 5px;
        flex-shrink: 0;
      }
      .arlo-opt-mono-pip {
        width: 9px;
        height: 9px;
        border-radius: 50%;
        background: var(--color-border, #d1d1d1);
        transition: background 0.15s;
        flex-shrink: 0;
      }
      .arlo-opt-mono-pip.filled {
        background: var(--color-foreground, #111);
      }

      /* ── Font Previewer ── */
      .arlo-opt-fp-input {
        width: 100%;
        padding: 16px;
        font-size: 36px;
        line-height: 1.4;
        border: 1px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        color: var(--color-foreground, #111);
        box-sizing: border-box;
        transition: font-family 0.15s;
        outline: none;
        white-space: nowrap;
        overflow: hidden;
        cursor: text;
      }
      /* Placeholder for contenteditable — 30% lighter than normal text */
      .arlo-opt-fp-input[data-placeholder]:empty::before {
        content: attr(data-placeholder);
        color: currentColor;
        opacity: 0.15;
        pointer-events: none;
        /* Keep the placeholder in its authored case even when the input forces
           ALL CAPS (text-transform on the input would otherwise uppercase it). */
        text-transform: none;
      }
      .arlo-opt-fp-input:focus { box-shadow: 0 0 0 2px var(--color-foreground, #111); }
      .arlo-opt-fp-input.arlo-opt-error { border: 1px solid var(--color-error, #c0392b); box-shadow: none; }
      .arlo-opt-fp-fonts {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 12px;
      }
      .arlo-opt-fp-font-btn {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        padding: 8px 12px;
        min-width: 72px;
        border: 1.5px solid var(--color-border, #d1d1d1);
        background: var(--color-background, #fff);
        cursor: pointer;
        border-radius: 4px;
        transition: border-color 0.15s;
        font-family: inherit;
      }
      .arlo-opt-fp-font-btn:hover { border-color: var(--color-foreground, #111); }
      .arlo-opt-fp-font-btn.active {
        border-color: var(--color-foreground, #111);
        border-width: 2px;
      }
      .arlo-opt-fp-font-sample { font-size: 26px; line-height: 1.2; }
      .arlo-opt-fp-font-name {
        font-size: 10px;
        color: var(--color-foreground-secondary, #666);
        text-align: center;
        max-width: 80px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: inherit;
      }

      /* ── Character counter ── */
      .arlo-opt-charcount {
        font-size: 12px;
        color: var(--color-foreground-secondary, #666);
        text-align: right;
        margin-top: 5px;
      }
      .arlo-opt-charcount.arlo-opt-charcount--warn {
        color: var(--color-error, #c0392b);
        font-weight: 600;
      }

      /* ── Error message ── */
      .arlo-opt-error-msg {
        font-size: 12px;
        color: var(--color-error, #c0392b);
        margin-top: 5px;
        display: none;
      }

      /* ── Hide Quantity label and Tax included note ── */
      .product-form__quantity { display: none !important; }
      .product__policies { display: none !important; }

      /* ── Quantity + Add to Cart on one row, always ── */
      .product-form__quantity-and-buy-buttons-wrapper {
        display: flex !important;
        flex-direction: row !important;
        align-items: flex-end !important;
        gap: 10px !important;
        flex-wrap: nowrap !important;
      }
      /* Quantity stepper widget (rendered via quantity-input snippet) */
      .product-form__quantity-and-buy-buttons-wrapper .quantity-input-wrapper--product-form {
        flex: 0 0 auto;
        width: auto !important;
        min-width: 0;
      }
      /* Hide the no-JS fallback quantity input — the stepper handles it */
      .product-form__quantity-and-buy-buttons-wrapper .product-form__quantity {
        display: none !important;
      }
      /* Hide the "Quantity" label above the stepper */
      .product-form__quantity-and-buy-buttons-wrapper .quantity-input-wrapper__labels {
        display: none !important;
      }
      /* ATC button group takes the rest of the row */
      .product-form__quantity-and-buy-buttons-wrapper .product-form__controls-group--submit {
        flex: 1 1 auto;
        min-width: 0;
      }
      /* Ensure the button itself fills its container */
      .product-form__quantity-and-buy-buttons-wrapper .product-form__cart-submit {
        width: 100% !important;
      }
    `;
    document.head.appendChild(s);
  }

  // ── Monogram text auto-shrink ─────────────────────────────────────────────────
  // Scales the input font-size down so text always fits within the box.
  // Uses a canvas to measure glyph widths + manual letter-spacing math.
  var _monoCanvas = null;
  function fitMonoInput(input) {
    var MAX_FS = 22;   // matches CSS default
    var MIN_FS = 9;
    var PADDING_H = 28; // 14px × 2 sides
    var LS_EM = 0.2;    // letter-spacing: 0.2em
    var TI_EM = 0.2;    // text-indent: 0.2em (visual compensation for trailing ls)

    var text = input.value || '';
    if (!text) { input.style.fontSize = ''; return; }

    var avail = (input.clientWidth || 200) - PADDING_H;
    if (avail <= 0) { input.style.fontSize = ''; return; }

    if (!_monoCanvas) _monoCanvas = document.createElement('canvas');
    var ctx = _monoCanvas.getContext('2d');

    // Binary search for largest font-size that fits
    var lo = MIN_FS, hi = MAX_FS, best = MIN_FS;
    while (lo <= hi) {
      var mid = (lo + hi) >> 1;
      ctx.font = mid + 'px "Times New Roman", Times, serif';
      var w = ctx.measureText(text).width
            + text.length * LS_EM * mid   // letter-spacing per char
            + TI_EM * mid;                // text-indent offset
      if (w <= avail) { best = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }

    input.style.fontSize = best + 'px';
  }

  // ── Monogram pip counter ──────────────────────────────────────────────────────
  function updateMonoPips(pipsEl, filled, max) {
    pipsEl.innerHTML = '';
    for (var i = 0; i < max; i++) {
      var pip = document.createElement('span');
      pip.className = 'arlo-opt-mono-pip' + (i < filled ? ' filled' : '');
      pipsEl.appendChild(pip);
    }
  }

  // ── Label helper ──────────────────────────────────────────────────────────────
  function makeLabel(opt) {
    var lbl = document.createElement('label');
    lbl.className = 'arlo-opt-label';
    // Always add a step badge; the coordinator assigns the correct number after
    // all scripts have rendered (see _arloStepCoord above).
    var badge = document.createElement('span');
    badge.className = 'arlo-opt-step-badge';
    badge.textContent = '';
    lbl.appendChild(badge);
    if (window._arloStepCoord) window._arloStepCoord.register(badge);
    var text = document.createTextNode(opt.label);
    lbl.appendChild(text);
    if (opt.required) {
      var star = document.createElement('span');
      star.className = 'arlo-opt-required';
      star.textContent = ' *';
      lbl.appendChild(star);
    }
    return lbl;
  }

  // ── Build and inject fields ───────────────────────────────────────────────────
  function buildFields(options, form) {
    // Remove any existing container — but first rescue the uploader slot if it was
    // moved inside (arlo-options.js loads twice due to canvas-capture + uploadcare-fields
    // both including it; getElementById returns null for detached nodes, so we must
    // re-attach the slot to the document before the container is removed).
    var existing = document.getElementById(CONTAINER_ID);
    if (existing) {
      var orphanSlot = existing.querySelector('#arlo-uploader-slot');
      if (orphanSlot) existing.parentNode.insertBefore(orphanSlot, existing);
      existing.remove();
    }

    var container = document.createElement('div');
    container.id = CONTAINER_ID;

    options.forEach(function (opt) {
      var wrap = document.createElement('div');
      wrap.className = 'arlo-opt';
      wrap.dataset.optId = opt.id;

      var err = document.createElement('div');
      err.className = 'arlo-opt-error-msg';

      if (opt.type === 'select') {
        var sel = document.createElement('select');
        sel.className = 'arlo-opt-select';
        sel.name = 'properties[' + opt.label + ']';
        sel.dataset.required = opt.required ? 'true' : 'false';

        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = opt.placeholder || ('Select ' + opt.label + '…');
        sel.appendChild(placeholder);

        (opt.values || []).forEach(function (val) {
          var o = document.createElement('option');
          o.value = val;
          o.textContent = val;
          sel.appendChild(o);
        });

        err.textContent = 'Please select an option';
        sel.addEventListener('change', function () {
          sel.classList.remove('arlo-opt-error');
          err.style.display = 'none';
        });

        if (opt.label) wrap.appendChild(makeLabel(opt));
        wrap.appendChild(sel);
        wrap.appendChild(err);

      } else if (opt.type === 'buttons') {
        var hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'properties[' + opt.label + ']';
        hidden.value = '';

        // Reuse the exact same grid + card classes as arlo-variant-buttons
        var btnsWrap = document.createElement('div');
        btnsWrap.className = 'arlo-opt-buttons arlo-vb-grid';
        // Single value → full width; multiple values → normal grid
        if ((opt.values || []).length === 1) {
          btnsWrap.style.gridTemplateColumns = '1fr';
        }

        err.textContent = 'Please select an option';

        // Shared bullets panel (same pattern as arlo-variant-buttons)
        var bulletsPanel = document.createElement('div');
        bulletsPanel.className = 'arlo-vb-bullets';
        var openBulletsLabel = null;

        // Use buttonValues (with description/bullets) if available, fall back to plain values
        var bvs = (opt.buttonValues && opt.buttonValues.length)
          ? opt.buttonValues
          : (opt.values || []).map(function (v) { return { label: v, description: '', bullets: [] }; });

        bvs.forEach(function (bv) {
          var val        = bv.label || '';
          var hasBullets = bv.bullets && bv.bullets.length;
          var hasImage   = bv.image && bv.image.trim();
          var hasPanel   = hasBullets || hasImage;

          var item = document.createElement('div');
          item.className = 'arlo-vb-item';

          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'arlo-opt-button arlo-vb-btn';
          if (hasPanel) btn.style.paddingRight = '26px';

          // Title
          var titleEl = document.createElement('div');
          titleEl.className = 'arlo-vb-title';
          titleEl.textContent = val;
          btn.appendChild(titleEl);

          // Description
          if (bv.description) {
            var descEl = document.createElement('div');
            descEl.className = 'arlo-vb-desc';
            descEl.textContent = bv.description;
            btn.appendChild(descEl);
          }

          // Price row (variant-replacement style)
          if (!bv.hidePrice && bv.addonPrice && !bv.price && !bv.salePrice) {
            var addonRowEl = document.createElement('div');
            addonRowEl.className = 'arlo-vb-price-row';
            var addonSpan = document.createElement('span');
            addonSpan.className = 'arlo-vb-price';
            addonSpan.textContent = '+ $' + parseFloat(bv.addonPrice).toFixed(2);
            addonRowEl.appendChild(addonSpan);
            btn.appendChild(addonRowEl);
          }
          if (!bv.hidePrice && (bv.price || bv.salePrice)) {
            var priceRowEl = document.createElement('div');
            priceRowEl.className = 'arlo-vb-price-row';
            if (bv.compareAtPrice) {
              var compareSpan = document.createElement('span');
              compareSpan.className = 'arlo-vb-compare';
              compareSpan.textContent = '$' + parseFloat(bv.compareAtPrice).toFixed(2);
              priceRowEl.appendChild(compareSpan);
            }
            var bvPriceDisplay = bv.salePrice
              ? '$' + parseFloat(bv.salePrice).toFixed(2)
              : '$' + parseFloat(bv.price).toFixed(2);
            var bvPriceSpan = document.createElement('span');
            bvPriceSpan.className = 'arlo-vb-price';
            bvPriceSpan.textContent = bvPriceDisplay;
            priceRowEl.appendChild(bvPriceSpan);
            btn.appendChild(priceRowEl);
          }

          // Discount badge — percentage as authored, or computed "Save $X" in dollar mode
          if (bv.discountBadge) {
            var bvBadge = document.createElement('span');
            bvBadge.className = 'arlo-vb-badge';
            if (bv.badgeStyle === 'dollar') _captureBasePrice();
            bvBadge.textContent = arloOptBadgeLabel(bv, _buttonBasePriceCents);
            btn.appendChild(bvBadge);
          }

          btn.addEventListener('click', function () {
            if (!opt.required && btn.classList.contains('active')) {
              // Toggle off (optional buttons only)
              btn.classList.remove('active');
              hidden.value = '';
              restoreOptButtonPagePrice();
            } else {
              // Undo previously active button's price
              var prevActive = btnsWrap.querySelector('.arlo-opt-button.active');
              if (prevActive && prevActive !== btn) {
                restoreOptButtonPagePrice();
              }
              btnsWrap.querySelectorAll('.arlo-opt-button').forEach(function (b) { b.classList.remove('active'); });
              btn.classList.add('active');
              hidden.value = val;
              btnsWrap.classList.remove('arlo-opt-error');
              err.style.display = 'none';
              // Apply price — bv.addonPrice if set, else fall back to opt-level linkedVariantPrice
              var _bvForPrice = (bv.addonPrice || bv.price || bv.salePrice || bv.parentQty) ? bv
                : Object.assign({}, bv, { addonPrice: opt.linkedVariantPrice ? opt.linkedVariantPrice / 100 : null });
              setOptButtonPagePrice(_bvForPrice);
              // Switch product image if configured
              if (bv.productImageIndex) setProductImageByIndex(bv.productImageIndex);
              // Set parent product quantity if configured
              if (bv.parentQty) {
                var qtyInput = form.querySelector('input[name="quantity"]') ||
                               form.querySelector('.quantity__input') ||
                               form.querySelector('[data-quantity-input]');
                if (qtyInput) {
                  qtyInput.value = bv.parentQty;
                  qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
                  qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
                }
              }
            }
          });
          btn._arloBv = bv;

          item.appendChild(btn);

          // "?" help badge for bullets and/or image
          if (hasPanel) {
            var helpBtn = document.createElement('button');
            helpBtn.type = 'button';
            helpBtn.className = 'arlo-vb-help';
            helpBtn.textContent = '?';
            helpBtn.setAttribute('aria-label', 'More info');
            helpBtn.addEventListener('click', function (e) {
              e.stopPropagation();
              if (openBulletsLabel === val) {
                openBulletsLabel = null;
                bulletsPanel.classList.remove('open');
                helpBtn.classList.remove('open');
              } else {
                btnsWrap.querySelectorAll('.arlo-vb-help.open').forEach(function (h) { h.classList.remove('open'); });
                openBulletsLabel = val;
                bulletsPanel.innerHTML = '';
                // Image (if set)
                if (hasImage) {
                  var img = document.createElement('img');
                  img.src = bv.image.trim();
                  img.style.cssText = 'width:100%;border-radius:6px;display:block;margin-bottom:' + (hasBullets ? '8px' : '0');
                  bulletsPanel.appendChild(img);
                }
                // Bullets (if set)
                if (hasBullets) {
                  var ul = document.createElement('ul');
                  (bv.bullets || []).forEach(function (b) {
                    var li = document.createElement('li');
                    li.textContent = b;
                    ul.appendChild(li);
                  });
                  bulletsPanel.appendChild(ul);
                }
                bulletsPanel.classList.add('open');
                helpBtn.classList.add('open');
              }
            });
            item.appendChild(helpBtn);
          }

          btnsWrap.appendChild(item);
        });

        // Pre-select a button if defaultSelected is set — defaultIndex chooses which
        // one (0-based; defaults to the first). Use click() so the full handler fires
        // (price, qty, image switching).
        if (opt.defaultSelected && bvs.length) {
          var _allBtns = btnsWrap.querySelectorAll('.arlo-opt-button');
          var _defIdx = parseInt(opt.defaultIndex, 10) || 0;
          var _defBtn = _allBtns[_defIdx] || _allBtns[0];
          if (_defBtn) _defBtn.click();
        }

        if (opt.label) wrap.appendChild(makeLabel(opt));
        wrap.appendChild(btnsWrap);
        wrap.appendChild(bulletsPanel);
        wrap.appendChild(hidden);
        wrap.appendChild(err);

      } else if (opt.type === 'tiles') {
        var hiddenT = document.createElement('input');
        hiddenT.type = 'hidden';
        hiddenT.name = 'properties[' + opt.label + ']';
        hiddenT.value = '';

        var tilesWrap = document.createElement('div');
        tilesWrap.className = 'arlo-opt-tiles';

        err.textContent = 'Please select an option';

        (opt.tileValues || []).forEach(function (tv) {
          var tile = document.createElement('button');
          tile.type = 'button';
          tile.className = 'arlo-opt-tile';
          tile.title = tv.value || '';

          var swatch = document.createElement('div');
          swatch.className = 'arlo-opt-tile-swatch';
          if (tv.fillType === 'image' && tv.image) {
            swatch.style.backgroundImage = 'url(' + tv.image + ')';
          } else {
            swatch.style.backgroundColor = tv.color || '#cccccc';
          }

          var tileLabel = document.createElement('span');
          tileLabel.className = 'arlo-opt-tile-label';
          tileLabel.textContent = tv.value || '';

          tile.appendChild(swatch);
          tile.appendChild(tileLabel);

          tile.addEventListener('click', function () {
            if (!opt.required && tile.classList.contains('active')) {
              // Deselect on second click (non-required only)
              tile.classList.remove('active');
              hiddenT.value = '';
            } else {
              tilesWrap.querySelectorAll('.arlo-opt-tile').forEach(function (t) { t.classList.remove('active'); });
              tile.classList.add('active');
              hiddenT.value = tv.value || '';
              tilesWrap.classList.remove('arlo-opt-error');
              err.style.display = 'none';
              // Switch product image if this tile is linked to one
              // console.log('[ArloOptions] tile clicked, linkedImageIndex=', tv.linkedImageIndex, 'linkedImageSrc=', tv.linkedImageSrc);
              if (tv.linkedImageIndex != null) {
                arloSwitchProductImage(tv.linkedImageIndex);
              } else if (tv.linkedImageSrc) {
                var srcBase = tv.linkedImageSrc.split('?')[0].split('/').pop().replace(/_\d+x[\d]*\.(jpg|jpeg|png|gif|webp)/i, '.$1').replace(/\.\w+$/, '');
                // console.log('[ArloOptions] looking for srcBase:', srcBase);
                // Search ONLY inside the product photo container to avoid uploadcare slots
                var photoContainer = document.querySelector(
                  '.product__media-container.above-mobile, .product__media-container.below-mobile'
                );
                var wrappers = photoContainer
                  ? photoContainer.querySelectorAll('[data-product-media-wrapper]')
                  : document.querySelectorAll('[data-product-media-wrapper]');
                // console.log('[ArloOptions] wrappers in photo container:', wrappers.length);
                var foundIdx = -1;
                wrappers.forEach(function (w, wi) {
                  if (foundIdx !== -1) return;
                  var img = w.querySelector('img');
                  if (!img) return;
                  // Check data-src (lazy-loaded) first, then fall back to src
                  var rawSrc = img.getAttribute('data-src') || img.getAttribute('data-srcset') || img.src || '';
                  // data-srcset may have multiple entries — grab the first URL
                  rawSrc = rawSrc.split(',')[0].trim().split(' ')[0];
                  var imgBase = rawSrc.split('?')[0].split('/').pop().replace(/_\d+x[\d]*\.(jpg|jpeg|png|gif|webp)/i, '.$1').replace(/\.\w+$/, '');
                  // console.log('[ArloOptions] wrapper', wi, 'imgBase:', imgBase);
                  if (imgBase === srcBase) foundIdx = wi;
                });
                // console.log('[ArloOptions] foundIdx:', foundIdx);
                if (foundIdx !== -1) arloSwitchProductImage(foundIdx);
              } else {
                // console.log('[ArloOptions] no linked image data on this tile');
              }
            }
          });

          tilesWrap.appendChild(tile);
        });

        if (opt.label) wrap.appendChild(makeLabel(opt));
        wrap.appendChild(tilesWrap);
        wrap.appendChild(hiddenT);
        wrap.appendChild(err);

      } else if (opt.type === 'monogram') {
        var maxLen = opt.maxChars || 7;
        // Allowed: A-Z, 0-9, ♥, &, space
        var MONO_ALLOWED = /^[A-Za-z0-9♥& ]$/;

        var monoHidden = document.createElement('input');
        monoHidden.type = 'hidden';
        monoHidden.name = 'properties[' + (opt.label || 'Monogram') + ']';
        monoHidden.value = '';

        var monoInput = document.createElement('input');
        monoInput.type = 'text';
        monoInput.className = 'arlo-opt-mono-input';
        monoInput.autocomplete = 'off';
        monoInput.autocapitalize = 'characters';
        monoInput.autocorrect = 'off';
        monoInput.spellcheck = false;
        monoInput.placeholder = '_ '.repeat(maxLen).trim();

        var pipsEl = document.createElement('div');
        pipsEl.className = 'arlo-opt-mono-pips';
        updateMonoPips(pipsEl, 0, maxLen);

        monoInput.addEventListener('input', function () {
          var raw = monoInput.value.toUpperCase();
          var filtered = '';
          var heartSeen = false;
          for (var ci = 0; ci < raw.length && filtered.length < maxLen; ci++) {
            var ch = raw[ci];
            if (ch === '♥') {
              if (heartSeen) continue; // only one heart allowed
              heartSeen = true;
            }
            if (MONO_ALLOWED.test(ch)) filtered += ch;
          }
          if (monoInput.value !== filtered) {
            var pos = monoInput.selectionStart - (monoInput.value.length - filtered.length);
            monoInput.value = filtered;
            monoInput.setSelectionRange(Math.max(0, pos), Math.max(0, pos));
          }
          monoHidden.value = filtered;
          updateMonoPips(pipsEl, filtered.length, maxLen);
          fitMonoInput(monoInput);
          monoInput.classList.remove('arlo-opt-error');
          err.style.display = 'none';
        });

        var heartBtn = document.createElement('button');
        heartBtn.type = 'button';
        heartBtn.className = 'arlo-opt-mono-heart';
        heartBtn.textContent = '♥︎'; // U+FE0E = text variation selector → forces black ♥, not red emoji
        heartBtn.title = 'Insert heart';
        heartBtn.addEventListener('click', function () {
          var pos = monoInput.selectionStart || monoInput.value.length;
          var current = monoInput.value;
          if (current.length >= maxLen) return;
          if (current.indexOf('♥') !== -1) return; // only one heart allowed
          var newVal = current.slice(0, pos) + '♥' + current.slice(pos);
          monoInput.value = newVal.slice(0, maxLen);
          monoHidden.value = monoInput.value;
          updateMonoPips(pipsEl, monoInput.value.length, maxLen);
          fitMonoInput(monoInput);
          monoInput.focus();
          monoInput.setSelectionRange(pos + 1, pos + 1);
        });

        var ampBtn = document.createElement('button');
        ampBtn.type = 'button';
        ampBtn.className = 'arlo-opt-mono-heart';
        ampBtn.textContent = '&';
        ampBtn.title = 'Insert &';
        ampBtn.addEventListener('click', function () {
          var pos = monoInput.selectionStart || monoInput.value.length;
          var current = monoInput.value;
          if (current.length >= maxLen) return;
          var newVal = current.slice(0, pos) + '&' + current.slice(pos);
          monoInput.value = newVal.slice(0, maxLen);
          monoHidden.value = monoInput.value;
          updateMonoPips(pipsEl, monoInput.value.length, maxLen);
          fitMonoInput(monoInput);
          monoInput.focus();
          monoInput.setSelectionRange(pos + 1, pos + 1);
        });

        var monoRow = document.createElement('div');
        monoRow.className = 'arlo-opt-mono-row';
        monoRow.appendChild(monoInput);
        monoRow.appendChild(heartBtn);
        monoRow.appendChild(ampBtn);
        // Required star sits at bottom-left of the input box, doesn't affect label line height
        if (opt.required) {
          var monoStar = document.createElement('span');
          monoStar.className = 'arlo-opt-mono-required';
          monoStar.textContent = '*';
          monoRow.appendChild(monoStar);
        }

        var hint = document.createElement('span');
        hint.className = 'arlo-opt-mono-hint';
        hint.textContent = 'A–Z · 0–9 · ♥︎ · & · space · up to ' + maxLen + ' characters'; // ︎ forces text (black) heart

        var footer = document.createElement('div');
        footer.className = 'arlo-opt-mono-footer';
        footer.appendChild(hint);
        footer.appendChild(pipsEl);

        err.textContent = 'Please enter your monogram';

        // Label without the * — star is shown at bottom-left of the input box instead
        // Always show step badge; fall back to 'Monogram' if no label set
        wrap.appendChild(makeLabel(Object.assign({}, opt, { required: false, label: opt.label || 'Monogram' })));
        wrap.appendChild(monoRow);
        wrap.appendChild(footer);
        wrap.appendChild(monoHidden);
        wrap.appendChild(err);

      } else if (opt.type === 'font-previewer') {
        // Font previewer — two cases:
        // Case 1 (multiple fonts): customer types text + picks font →
        //   properties[label] = text, properties[fontPropertyLabel] = font display name
        // Case 2 (single fixed font): styled text input previewing what they'll receive →
        //   properties[label] = text only

        var fpLabel = opt.textPropertyLabel || opt.label || 'Personalisation';
        var fpPlaceholder = opt.placeholder || 'Type here to preview…';
        var fpFontPropLabel = opt.fontPropertyLabel || 'Font style & size';

        // Resolve font ids against the global font registry
        var allFonts = (window.ArloProductConfig && window.ArloProductConfig.fonts) || [];
        var fpFontIds = opt.fonts || [];
        var resolvedFonts = fpFontIds.map(function (fid) {
          return allFonts.find(function (f) { return f.id === fid; }) ||
                 { id: fid, name: fid, cssClass: fid, displayName: '' };
        });

        var fpHidden = document.createElement('input');
        fpHidden.type = 'hidden';
        fpHidden.name = 'properties[' + fpLabel + ']';
        fpHidden.value = '';
        fpHidden.dataset.required = opt.required ? 'true' : 'false';

        var fpFontHidden = null;

        // contenteditable div — avoids browser clipping of script font ascenders/descenders
        var fpInput = document.createElement('div');
        fpInput.contentEditable = 'true';
        fpInput.className = 'arlo-opt-fp-input';
        fpInput.dataset.placeholder = fpPlaceholder;
        fpInput.spellcheck = false;
        var fpWrap = null; // set in canvas-png branch; wrapper div used there instead

        // ── Apply input box styling ──────────────────────────────────────────
        if (opt.outputFormat === 'canvas-png' && opt.canvasSettings) {
          // Canvas PNG mode: the input box mirrors the artboard so the customer
          // sees exactly what the final product will look like.
          var cs = opt.canvasSettings;
          var wMm   = cs.templateWidthMm  || 150;
          var hMm   = cs.templateHeightMm || 50;
          var fsPt  = cs.fontSize  || 60;
          var dpi   = cs.dpi       || 300;
          var hAlign = cs.hAlign  || 'center';
          var vAlign = cs.vAlign  || 'middle';
          var vOff   = cs.vOffset || 0;
          var kerning = cs.kerning || 0;
          var leadingMult = cs.leading || 1.4;

          // 1. Wrapper is a fixed-size artboard frame (aspect-ratio box).
          //    fpInput is absolutely positioned inside it, so it can never
          //    overflow the frame regardless of how much text is typed.
          var fpWrap = document.createElement('div');
          var isCircleBox = cs.shape === 'circle';
          fpWrap.style.aspectRatio  = wMm + ' / ' + hMm;
          fpWrap.style.height       = 'auto';
          fpWrap.style.width        = '100%';
          fpWrap.style.position     = 'relative';
          fpWrap.style.background   = 'var(--color-background, #f8f8f8)';
          fpWrap.style.border       = '1px solid rgba(var(--color-foreground, 18,18,18), 0.2)';
          fpWrap.style.borderRadius = isCircleBox ? '50%' : '4px';
          fpWrap.style.overflow     = 'hidden';
          fpWrap.style.cursor       = 'text';
          fpWrap.style.marginLeft   = 'auto';
          fpWrap.style.marginRight  = 'auto';

          // Optional width cap — percentage of the container (100 = full width, 60 = 60%).
          // Height auto-follows from the aspect-ratio, so the box stays proportional.
          var displayW = cs.displayWidthPct || 100;
          if (displayW < 100) {
            fpWrap.style.maxWidth = displayW + '%';
          }

          // Clicking anywhere in the wrapper focuses the inner input
          fpWrap.addEventListener('click', function () { fpInput.focus(); });
          // Forward focus ring to wrapper
          fpInput.addEventListener('focus', function () { fpWrap.style.boxShadow = '0 0 0 2px var(--color-foreground, #111)'; });
          fpInput.addEventListener('blur',  function () { fpWrap.style.boxShadow = ''; });

          // 2. A non-interactive flex container sits between fpWrap and fpInput.
          //    It handles vertical alignment without putting display:flex on the
          //    contenteditable itself (which would misplace the cursor).
          var fpFlex = document.createElement('div');
          fpFlex.style.position        = 'absolute';
          // Circle: inset the text area ~15% from each edge so text stays within
          // the safe inscribed zone and doesn't get clipped by the curved edges.
          var _circleInset = isCircleBox ? '15%' : '0';
          fpFlex.style.top             = _circleInset;
          fpFlex.style.bottom          = _circleInset;
          fpFlex.style.left            = _circleInset;
          fpFlex.style.right           = _circleInset;
          fpFlex.style.display         = 'flex';
          fpFlex.style.flexDirection   = 'column';
          fpFlex.style.justifyContent  = vAlign === 'top'    ? 'flex-start'
                                       : vAlign === 'bottom' ? 'flex-end'
                                       : 'center';
          fpFlex.style.overflow        = 'hidden';
          fpFlex.style.pointerEvents   = 'none'; // clicks fall through to fpWrap handler

          // 3. fpInput is a normal block inside fpFlex — no flex on it, cursor works correctly.
          fpInput.style.width         = '100%';
          fpInput.style.maxHeight     = '100%'; // can never overflow fpFlex
          fpInput.style.boxSizing     = 'border-box';
          fpInput.style.pointerEvents = 'auto';
          fpInput.style.background    = 'transparent';
          fpInput.style.border        = 'none';
          fpInput.style.boxShadow     = 'none';
          fpInput.style.whiteSpace    = 'pre-wrap';
          fpInput.style.wordBreak     = 'normal';
          fpInput.style.overflowWrap  = 'break-word';
          fpInput.style.hyphens       = 'none';
          fpInput.style.overflow      = 'hidden';
          // The base .arlo-opt-fp-input CSS class hardcodes line-height:1.4 — override
          // with the admin's configured Line Spacing value so it actually takes effect.
          fpInput.style.lineHeight    = leadingMult + '';

          // 3. Text formatting from canvas settings
          fpInput.style.textAlign      = hAlign;
          if (cs.color)  fpInput.style.color         = cs.color;
          if (kerning)   fpInput.style.letterSpacing  = kerning + 'px';

          // 4. Scale font size proportionally to rendered box height
          //    pt → fraction of artboard height: fsPt / (hMm/25.4*72)
          var artboardHPt    = hMm / 25.4 * 72;
          var artboardFullPx = (hMm / 25.4) * dpi;
          var fontFraction   = fsPt / artboardHPt;

          function _applyCanvasBoxFont() {
            var w = fpWrap.offsetWidth;
            if (!w) return;
            // Set an explicit pixel height so the box never grows with content.
            // aspect-ratio alone doesn't hard-lock height; content can push it open.
            var h = Math.round(w * hMm / wMm);
            fpWrap.style.height = h + 'px';
            var fsPx = Math.round(h * fontFraction);
            fpInput.style.fontSize = fsPx + 'px';

            // Vertical alignment is handled by fpFlex (justify-content).
            // Padding here is for side insets + vOffset nudge only.
            // IMPORTANT: must scale with fsPx (not a fixed px value) so the amount
            // of text that fits stays identical regardless of the box's on-screen
            // width (Display Width % / container size / screen rotation). A fixed
            // px pad eats a bigger share of a smaller box, shrinking usable text
            // area disproportionately and changing line-fit at different widths.
            // 0.5 matches edgePad's factor in renderTextToCanvas() (the actual PNG
            // export), so the live box's capacity also mirrors the real print output.
            var sidePad = Math.round(fsPx * 0.5);
            var vOffDisplay = vOff ? Math.round(vOff * h / artboardFullPx) : 0;
            fpInput.style.paddingTop    = Math.max(0, sidePad + vOffDisplay) + 'px';
            fpInput.style.paddingBottom = Math.max(0, sidePad - vOffDisplay) + 'px';
            fpInput.style.paddingLeft   = sidePad + 'px';
            fpInput.style.paddingRight  = sidePad + 'px';
          }

          if (window.ResizeObserver) {
            var _ro = new ResizeObserver(_applyCanvasBoxFont);
            _ro.observe(fpWrap);
          } else {
            setTimeout(_applyCanvasBoxFont, 60);
          }

          fpFlex.appendChild(fpInput);
          fpWrap.appendChild(fpFlex);

        } else {
          // Text mode — use the manually configured inputStyle settings
          var is = opt.inputStyle || {};
          if (is.fontSize)  fpInput.style.fontSize  = is.fontSize + 'px';
          if (is.textAlign) fpInput.style.textAlign = is.textAlign;
          if (is.padding)   fpInput.style.padding   = is.padding;
          if (is.height)    fpInput.style.height    = is.height + 'px';

          // Vertical offset — shift text up/down by adjusting padding
          var vOffset = opt.verticalOffset || 0;
          if (vOffset) {
            var basePad = is.padding ? parseInt(is.padding) : 16;
            fpInput.style.paddingTop    = Math.max(0, basePad + vOffset) + 'px';
            fpInput.style.paddingBottom = Math.max(0, basePad - vOffset) + 'px';
          }
        }

        // Apply initial font directly to the input
        var activeCss = resolvedFonts.length ? resolvedFonts[0].id : '';
        if (activeCss) fpInput.style.fontFamily = "'" + activeCss + "'";

        // All-caps mode — display via CSS (keeps the caret smooth while typing);
        // the actual submitted value + canvas text are uppercased at read time.
        var fpUpper = !!opt.uppercase;
        if (fpUpper) fpInput.style.textTransform = 'uppercase';

        // Canvas-png mode allows Enter (multi-line like InDesign text box).
        // Text mode keeps single-line behaviour.
        var fpIsCanvas = opt.outputFormat === 'canvas-png' && opt.canvasSettings;

        // Small tolerance for the artboard-overflow guard below. Browsers' actual
        // rendered line-box height is often a px or two taller than the CSS
        // line-height implies (font metrics/ascent-descent), so on tight artboards
        // (e.g. a 2-line message that mathematically just fits) scrollHeight can
        // tick past clientHeight before the line is even visible — blocking input
        // while there's still visible room in the box. This buffer avoids that
        // premature/hair's-breadth block without disabling the real overflow guard.
        var CANVAS_OVERFLOW_TOLERANCE = 3;

        // Honour maxChars in all modes (canvas-png and text).
        // In canvas-png the artboard clips overflow visually, but an explicit
        // maxChars limit from the admin is always enforced.
        var fpMaxChars = opt.maxChars ? opt.maxChars : null;

        var fpCounter = null;
        if (fpMaxChars) {
          fpCounter = document.createElement('div');
          fpCounter.className = 'arlo-opt-charcount';
          fpCounter.textContent = '0 / ' + fpMaxChars;
        }
        fpInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' && !fpIsCanvas) { e.preventDefault(); return; }
          // Hard-stop at maxChars — block printable keys when limit is reached,
          // but allow navigation, deletion, cut, copy, select-all, etc.
          if (fpMaxChars && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
            var cur = fpInput.textContent.replace(/[\r\n]/g, '');
            var sel = window.getSelection ? window.getSelection().toString() : '';
            if (cur.length >= fpMaxChars && !sel) {
              e.preventDefault();
              return;
            }
          }
          // Canvas-png: block characters and Enter once text fills the artboard height.
          //
          // IMPORTANT: compare against fpFlex.clientHeight, NOT fpInput.clientHeight.
          // fpInput has no explicit height — only a maxHeight cap — so its clientHeight
          // just tracks the CURRENT content's height and stays equal to scrollHeight
          // right up until the box is genuinely full. fpFlex, on the other hand, has a
          // real pinned pixel height from _applyCanvasBoxFont(), so it's the box's true,
          // fixed ceiling regardless of how much has been typed so far.
          //
          // Enter is checked PROACTIVELY (current height + one more line-height) rather
          // than just the current state — otherwise an Enter that currently "just barely
          // passes" drops the cursor onto a brand-new line that immediately exceeds the
          // limit, so every following keystroke on that line gets silently blocked. That
          // leaves a blinking cursor customers can never type into — looks like a broken
          // page, not a reached limit. Blocking Enter itself up front prevents that dead
          // line from ever being created in the first place.
          if (fpIsCanvas && (e.key.length === 1 || e.key === 'Enter') && !e.ctrlKey && !e.metaKey && !e.altKey) {
            var sel = window.getSelection ? window.getSelection().toString() : '';
            var capacity = fpFlex.clientHeight || fpInput.clientHeight;
            var projectedHeight = fpInput.scrollHeight;
            if (e.key === 'Enter') {
              var lh = parseFloat(window.getComputedStyle(fpInput).lineHeight) || 0;
              projectedHeight += lh;
            }
            if (!sel && projectedHeight > capacity + CANVAS_OVERFLOW_TOLERANCE) {
              e.preventDefault();
              return;
            }
          }
          // Canvas-png: prevent the browser from auto-scrolling the page to follow
          // the caret when it moves below the clipped artboard overflow area.
          if (fpIsCanvas) {
            var _sy = window.scrollY;
            requestAnimationFrame(function () {
              if (window.scrollY !== _sy) window.scrollTo(window.scrollX, _sy);
            });
          }
        });

        // Strip HTML on paste; enforce maxChars and artboard overflow
        fpInput.addEventListener('paste', function (e) {
          e.preventDefault();
          var text = (e.clipboardData || window.clipboardData).getData('text/plain');
          if (!fpIsCanvas) text = text.replace(/[\r\n]/g, '');
          if (fpMaxChars) {
            var cur = fpInput.textContent.replace(/[\r\n]/g, '');
            var sel = window.getSelection ? window.getSelection().toString() : '';
            var used = cur.length - sel.length;
            var remaining = Math.max(0, fpMaxChars - used);
            text = text.slice(0, remaining);
          }
          document.execCommand('insertText', false, text);
          // Canvas-png: trim pasted content that overflows the artboard.
          // Same fpFlex.clientHeight reasoning as the keydown handler above.
          if (fpIsCanvas) {
            requestAnimationFrame(function () {
              var capacity = fpFlex.clientHeight || fpInput.clientHeight;
              while (fpInput.scrollHeight > capacity + CANVAS_OVERFLOW_TOLERANCE && fpInput.textContent.length > 0) {
                fpInput.textContent = fpInput.textContent.slice(0, -1);
              }
              fpHidden.value = fpUpper ? fpInput.textContent.toUpperCase() : fpInput.textContent;
            });
          }
        });

        fpInput.addEventListener('input', function () {
          // Canvas-png keeps newlines; text mode collapses to single line
          var text = fpIsCanvas
            ? fpInput.textContent
            : fpInput.textContent.replace(/[\r\n]/g, '');
          if (fpMaxChars && text.replace(/[\r\n]/g, '').length > fpMaxChars) {
            text = text.slice(0, fpMaxChars);
          }
          if (fpInput.textContent !== text) {
            fpInput.textContent = text;
            var range = document.createRange();
            var sel = window.getSelection();
            range.selectNodeContents(fpInput);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          }
          fpHidden.value = fpUpper ? text.toUpperCase() : text;
          fpInput.classList.remove('arlo-opt-error');
          err.style.display = 'none';
          if (fpCounter) {
            var len = text.replace(/[\r\n]/g, '').length;
            fpCounter.textContent = len + ' / ' + fpMaxChars;
            fpCounter.classList.toggle('arlo-opt-charcount--warn', len >= fpMaxChars);
          }
        });

        err.textContent = 'Please enter your text';

        // Always show a step label for canvas-png inputs — fall back to a generic label
        // so the step badge is always registered with the coordinator even if no label is set.
        wrap.appendChild(makeLabel(Object.assign({}, opt, { label: opt.label || 'Personalised message', required: false })));

        // Track the currently selected font — shared by font buttons + preview button.
        // Starts as the first (or only) font; updated when a font button is clicked.
        var currentFontConfig = resolvedFonts.length ? resolvedFonts[0] : null;

        // Font selector buttons — only shown for Case 1 (multiple fonts configured)
        if (resolvedFonts.length > 1) {
          fpFontHidden = document.createElement('input');
          fpFontHidden.type = 'hidden';
          fpFontHidden.name = 'properties[' + fpFontPropLabel + ']';
          fpFontHidden.value = resolvedFonts[0].displayName || resolvedFonts[0].name;

          var fpFontRow = document.createElement('div');
          fpFontRow.className = 'arlo-opt-fp-fonts';

          resolvedFonts.forEach(function (f, fi) {
            var css = f.id; // use font id — matches the name registered by arlo-font-selector.js via FontFace API
            var fontBtn = document.createElement('button');
            fontBtn.type = 'button';
            fontBtn.className = 'arlo-opt-fp-font-btn' + (fi === 0 ? ' active' : '');
            fontBtn.title = f.displayName || f.name;

            var sample = document.createElement('span');
            sample.className = 'arlo-opt-fp-font-sample';
            sample.style.fontFamily = "'" + css + "'";
            sample.textContent = 'Aa';

            var lbl = document.createElement('span');
            lbl.className = 'arlo-opt-fp-font-name';
            lbl.textContent = f.displayName || f.name;

            fontBtn.appendChild(sample);
            fontBtn.appendChild(lbl);

            fontBtn.addEventListener('click', function () {
              fpFontRow.querySelectorAll('.arlo-opt-fp-font-btn').forEach(function (b) { b.classList.remove('active'); });
              fontBtn.classList.add('active');
              fpInput.style.fontFamily = "'" + css + "'";
              if (fpFontHidden) fpFontHidden.value = f.displayName || f.name;
              currentFontConfig = f; // keep preview button in sync
            });

            fpFontRow.appendChild(fontBtn);
          });

          wrap.appendChild(fpFontRow);
          wrap.appendChild(fpFontHidden);
        }

        wrap.appendChild(fpWrap || fpInput);
        if (fpCounter) wrap.appendChild(fpCounter);


        wrap.appendChild(fpHidden);
        wrap.appendChild(err);

      } else if (opt.type === 'uploadcare') {
        // Move the uploader slot into this step so it appears at the right position.
        var ucSlot = document.getElementById('arlo-uploader-slot');
        if (ucSlot) {
          ucSlot.style.display = '';
          wrap.appendChild(ucSlot);
        }
        // No hidden input needed here — arlo-upload-value is inside the slot.

      } else if (opt.type === 'checkbox') {
        // Checkbox as a hidden input + visual toggle
        var cbWrap = document.createElement('label');
        cbWrap.className = 'arlo-opt-checkbox-wrap';

        var cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.className = 'arlo-opt-checkbox';

        // Hidden input that carries the value — always present so Shopify sees it
        var cbHidden = document.createElement('input');
        cbHidden.type = 'hidden';
        cbHidden.name = 'properties[' + opt.label + ']';
        cbHidden.value = '';

        err.textContent = 'This option is required';

        cb.addEventListener('change', function () {
          cbHidden.value = cb.checked ? (opt.checkedValue || 'Yes') : '';
          cbWrap.classList.remove('arlo-opt-error');
          err.style.display = 'none';
        });

        var cbLabel = document.createElement('span');
        cbLabel.className = 'arlo-opt-checkbox-label';
        cbLabel.textContent = opt.label;
        if (opt.required) {
          var starCb = document.createElement('span');
          starCb.className = 'arlo-opt-required';
          starCb.textContent = ' *';
          cbLabel.appendChild(starCb);
        }

        cbWrap.appendChild(cb);
        cbWrap.appendChild(cbLabel);
        wrap.appendChild(cbWrap);
        wrap.appendChild(cbHidden);
        wrap.appendChild(err);
      }

      container.appendChild(wrap);
    });

    // Insert before the qty+ATC wrapper (keeps options full-width, above the flex row)
    var qtyWrapper = form.querySelector('.product-form__quantity-and-buy-buttons-wrapper');
    if (qtyWrapper && qtyWrapper.parentNode) {
      qtyWrapper.parentNode.insertBefore(container, qtyWrapper);
    } else {
      // Fallback: before the ATC button's parent
      var atcBtn = form.querySelector('[data-add-to-cart]') ||
                   form.querySelector('[name="add"]') ||
                   form.querySelector('[type="submit"]');
      if (atcBtn && atcBtn.parentNode) {
        atcBtn.parentNode.insertBefore(container, atcBtn);
      } else {
        form.appendChild(container);
      }
    }

    return container;
  }

  // ── Validate required fields ──────────────────────────────────────────────────
  function validateOptions(options) {
    var valid = true;
    var container = document.getElementById(CONTAINER_ID);
    if (!container) return true;

    options.forEach(function (opt) {
      if (!opt.required) return;
      var wrap = container.querySelector('[data-opt-id="' + opt.id + '"]');
      if (!wrap) return;

      var errEl = wrap.querySelector('.arlo-opt-error-msg');

      if (opt.type === 'select') {
        var sel = wrap.querySelector('select');
        if (sel && !sel.value) {
          sel.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          if (valid) sel.focus();
          valid = false;
        }
      } else if (opt.type === 'buttons') {
        var bWrap = wrap.querySelector('.arlo-opt-buttons');
        var bHidden = wrap.querySelector('input[type="hidden"]');
        if (bHidden && !bHidden.value) {
          if (bWrap) bWrap.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          valid = false;
        }
      } else if (opt.type === 'tiles') {
        var tWrap = wrap.querySelector('.arlo-opt-tiles');
        var tHidden = wrap.querySelector('input[type="hidden"]');
        if (tHidden && !tHidden.value) {
          if (tWrap) tWrap.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          valid = false;
        }
      } else if (opt.type === 'monogram') {
        var mHidden = wrap.querySelector('input[type="hidden"]');
        var mInput = wrap.querySelector('.arlo-opt-mono-input');
        if (mHidden && !mHidden.value.trim()) {
          if (mInput) mInput.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          if (valid && mInput) mInput.focus();
          valid = false;
        }
      } else if (opt.type === 'checkbox') {
        var cb = wrap.querySelector('input[type="checkbox"]');
        var cbWrapEl = wrap.querySelector('.arlo-opt-checkbox-wrap');
        if (cb && !cb.checked) {
          if (cbWrapEl) cbWrapEl.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          valid = false;
        }
      } else if (opt.type === 'font-previewer') {
        var fpEl = wrap.querySelector('.arlo-opt-fp-input');
        var fpH = wrap.querySelector('input[type="hidden"][name^="properties["]');
        if (fpEl && !fpEl.textContent.trim()) {
          fpEl.classList.add('arlo-opt-error');
          if (errEl) errEl.style.display = 'block';
          if (valid) fpEl.focus();
          valid = false;
        }
      }
    });

    // Photo uploader — when required, EVERY slot must be filled (not just one).
    // arlo-options intercepts the submit and bypasses the uploader's own check,
    // so we validate it here against the current (possibly per-variant) max count.
    var ucOpt = options.find(function (o) { return o.type === 'uploadcare' && o.required; });
    if (ucOpt) {
      var uploaderRoot = document.getElementById('arlo-uploader');
      if (uploaderRoot) {
        var need = parseInt(uploaderRoot.dataset.maxImages, 10) || 1;
        var have;
        if (need <= 1) {
          var single = document.getElementById('arlo-upload-value');
          have = (single && single.value) ? 1 : 0;
        } else {
          // One properties[Photo N] input is written per completed upload.
          have = document.querySelectorAll('.arlo-photo-prop[name^="properties[Photo"]').length;
        }
        if (have < need) {
          valid = false;
          showUploaderError(uploaderRoot, need, have);
          uploaderRoot.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          clearUploaderError(uploaderRoot);
        }
      }
    }

    return valid;
  }

  function showUploaderError(root, need, have) {
    var msg = document.getElementById('arlo-uploader-req-msg');
    if (!msg) {
      msg = document.createElement('div');
      msg.id = 'arlo-uploader-req-msg';
      msg.style.cssText = 'color:var(--color-error,#c0392b);font-size:13px;font-weight:600;margin-top:8px';
      (root.parentNode || root).insertBefore(msg, root.nextSibling);
    }
    msg.textContent = 'Please upload all ' + need + ' photos to continue (' + have + ' of ' + need + ' added).';
    msg.style.display = 'block';
  }

  function clearUploaderError(root) {
    var msg = document.getElementById('arlo-uploader-req-msg');
    if (msg) msg.style.display = 'none';
  }

  // ── Suppress native Shopify & app option selectors for Arlo-configured products ──
  function suppressNativeOptions() {
    if (document.getElementById('arlo-options-suppress')) return;
    var s = document.createElement('style');
    s.id = 'arlo-options-suppress';
    // All rules scoped to html.arlo-managed so they stop applying the moment
    // that class is removed (e.g. AJAX navigation to a non-Arlo product).
    s.textContent = [
      // Artisan theme custom option blocks — outer wrapper (label + input)
      'html.arlo-managed .product__controls-group--text { display: none !important; }',
      'html.arlo-managed .product__controls-group--textarea { display: none !important; }',
      'html.arlo-managed .product__controls-group--checkbox { display: none !important; }',
      'html.arlo-managed .product__controls-group--list { display: none !important; }',
      // Inner input area
      'html.arlo-managed .product__custom-option { display: none !important; }',
      'html.arlo-managed .product__custom-option-instructions { display: none !important; }',
      // Hidden form inputs (prevent blank order properties)
      'html.arlo-managed .product-form__custom-option { display: none !important; }',
      // Old arlo-font-selector.js font picker
      'html.arlo-managed .arlo-font-selector { display: none !important; }',
      // Common Shopify app option injectors
      'html.arlo-managed .infiniteoptions-container { display: none !important; }',
      'html.arlo-managed [class*="iO-option"] { display: none !important; }',
      'html.arlo-managed .BOLD_options { display: none !important; }',
      'html.arlo-managed .bold-options { display: none !important; }',
      'html.arlo-managed .gpotf-options-form { display: none !important; }',
      'html.arlo-managed [id^="shoppad-"] { display: none !important; }',
      'html.arlo-managed .sc-product-option { display: none !important; }',
      // SPB app option fields (label uses spb-productdesc*, input uses spb-productopt*)
      'html.arlo-managed [class*="spb-product"] { display: none !important; }',
      'html.arlo-managed .spb-helptext { display: none !important; }',
    ].join('\n');
    document.head.appendChild(s);

    // Hide and disable any third-party app property inputs not inside our own container.
    // Works regardless of what CSS classes the app uses (SPB, InfiniteOptions, etc.)
    document.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if (el.closest('#' + CONTAINER_ID)) return; // skip our own inputs
      if (el.closest('#arlo-uploader-slot')) return; // skip photo uploader hidden input — buildFields will reposition it
      el.disabled = true;
      el.removeAttribute('name');
    });

    // Nuclear suppression: hide any direct child of the form that contains
    // legacy properties[] inputs (catches fieldset/span/div wrappers regardless of class)
    var productForm = document.querySelector('form[action*="/cart/add"], form[data-product-form]');
    if (productForm) {
      Array.prototype.forEach.call(productForm.children, function (child) {
        if (child.id === CONTAINER_ID) return; // never hide our own container
        if (child.id === 'arlo-uploader-slot') return; // skip — buildFields will reposition this
        if (child.querySelector('[name^="properties["]') ||
            child.querySelector('[data-custom-option-target]')) {
          child.style.cssText = 'display:none!important';
        }
      });
    }

    // Also disable Artisan hidden form inputs
    document.querySelectorAll('[data-custom-option-target]').forEach(function (el) {
      el.disabled = true;
      el.removeAttribute('name');
    });

    // Remove empty <label> elements left by native Shopify/Artisan option blocks
    document.querySelectorAll('fieldset label').forEach(function (lbl) {
      if (!lbl.textContent.trim() && !lbl.querySelector('input, select, textarea')) {
        lbl.remove();
      }
    });
  }

  // ── Collect all line-item properties for the main product ────────────────────
  // Gathers hidden properties[...] inputs from BOTH the arlo-options container AND
  // the cart form itself. The photo uploader (arlo-photo-uploader.js) appends its
  // per-photo display props (properties[Photo 1], [Photo 2], …), the multi-image
  // group URL (_Photo Group), and the circle flag directly onto the form — outside
  // our container. If we only read the container, multi-image cart previews are lost.
  function collectMainProperties() {
    var props = {};
    var container = document.getElementById(CONTAINER_ID);
    if (container) {
      container.querySelectorAll('input[type="hidden"][name^="properties["]').forEach(function (inp) {
        var m = inp.name.match(/^properties\[(.+)\]$/);
        if (m && inp.value) props[m[1]] = inp.value;
      });
    }
    var form = document.querySelector('form[action*="/cart/add"], form[data-product-form]');
    if (form) {
      form.querySelectorAll('.arlo-photo-prop[name^="properties["], #arlo-upload-value[name^="properties["]').forEach(function (inp) {
        var m = inp.name.match(/^properties\[(.+)\]$/);
        if (m && inp.value) props[m[1]] = inp.value;
      });
    }
    return props;
  }

  // ── Add linked products to cart for checked checkboxes / selected buttons ─────
  function addLinkedProducts(options) {
    var container = document.getElementById(CONTAINER_ID);
    if (!container) return Promise.resolve();

    var promises = [];
    options.forEach(function (opt) {
      var wrap = container.querySelector('[data-opt-id="' + opt.id + '"]');
      if (!wrap) return;

      if (opt.type === 'checkbox') {
        // Checkbox: option-level linkedVariantId only
        if (!opt.linkedVariantId) return;
        var cb = wrap.querySelector('input[type="checkbox"]');
        if (!cb || !cb.checked) return;
        promises.push(fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: opt.linkedVariantId, quantity: opt.linkedVariantQty || 1 }),
        }).catch(function (err) { console.warn('[ArloOptions] Failed to add linked product:', err); }));

      } else if (opt.type === 'buttons') {
        // Buttons: per-button-value linkedVariantId takes priority; fall back to option-level
        var activeBtn = wrap.querySelector('.arlo-opt-button.active');
        if (!activeBtn) return;
        var bv = activeBtn._arloBv || {};
        var variantId = bv.linkedVariantId || opt.linkedVariantId;
        if (!variantId) return;
        var qty = parseInt(bv.linkedVariantQty, 10) || opt.linkedVariantQty || 1;
        promises.push(fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty }),
        }).catch(function (err) { console.warn('[ArloOptions] Failed to add linked product:', err); }));
      }
    });

    return Promise.all(promises);
  }

  // ── Hook cart form ────────────────────────────────────────────────────────────
  function hookForm(options, form) {
    var canvasOptions = options.filter(function (o) {
      return o.type === 'font-previewer' && o.outputFormat === 'canvas-png';
    });
    var hasCanvas = canvasOptions.length > 0;
    var _linkedSubmitPending = false;

    function handleSubmit(e, isBtn) {
      // Skip if we re-dispatched after linked products finished — let theme handle it
      if (_linkedSubmitPending) return;

      if (!validateOptions(options)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var container = document.getElementById(CONTAINER_ID);
        if (container) container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      var hasLinked = options.some(function (opt) {
        var c = document.getElementById(CONTAINER_ID);
        if (!c) return false;
        var wrap = c.querySelector('[data-opt-id="' + opt.id + '"]');
        if (!wrap) return false;
        if (opt.type === 'checkbox') {
          if (!opt.linkedVariantId) return false;
          var cb = wrap.querySelector('input[type="checkbox"]');
          return cb && cb.checked;
        }
        if (opt.type === 'buttons') {
          // Check button-value-level OR option-level linked variant
          var activeBtn = wrap.querySelector('.arlo-opt-button.active');
          if (!activeBtn) return false;
          var bv = activeBtn._arloBv || {};
          return !!(bv.linkedVariantId || opt.linkedVariantId);
        }
        return false;
      });

      if (hasCanvas) {
        e.preventDefault();
        e.stopImmediatePropagation();

        var atcBtnC = form.querySelector('[data-add-to-cart]');
        if (atcBtnC) atcBtnC.classList.add('loading');

        // Generate the artwork PNG(s) FIRST (sets the hidden property inputs),
        // THEN add the main product + any linked products together in one batch
        // and open the cart once. This avoids the earlier double-open glitch where
        // the linked product was added, the cart popped open, and then a separate
        // form.submit() for the main product closed and re-opened it.
        runCanvasCapture(options, form, canvasOptions, function () {
          var variantEl = form.querySelector('[name="id"]');
          var mainVariantId = variantEl ? parseInt(variantEl.value, 10) : null;
          var qtyEl = form.querySelector('[name="quantity"]');
          var mainQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

          var mainProperties = collectMainProperties();

          var mainBody = { id: mainVariantId, quantity: mainQty };
          if (Object.keys(mainProperties).length) mainBody.properties = mainProperties;

          // Add the main product first, then the linked products, so the main
          // (personalised) item sits at the top of the cart.
          var mainPromise = mainVariantId ? fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(mainBody),
          }).catch(function (err) { console.warn('[ArloOptions] Failed to add main product:', err); }) : Promise.resolve();

          mainPromise.then(function () {
            return addLinkedProducts(options);
          }).then(function () {
            if (atcBtnC) atcBtnC.classList.remove('loading');
            if (window.emit) {
              window.emit('quick-cart:updated');
              window.emit('quick-cart:open');
            }
            document.dispatchEvent(new CustomEvent('cart:item-added'));
          }).catch(function (err) {
            if (atcBtnC) atcBtnC.classList.remove('loading');
            console.warn('[ArloOptions] cart add failed:', err);
          });
        });
        return;
      }

      if (hasLinked) {
        // Add the main product and all linked products directly via fetch,
        // then trigger cart refresh manually. We avoid re-dispatching the
        // form submit because the theme's variant-lookup chain is fragile for
        // single-variant / single-option products.
        e.preventDefault();
        e.stopImmediatePropagation();

        // Collect main product info now (before async ops can change it)
        var variantEl = form.querySelector('[name="id"]');
        var mainVariantId = variantEl ? parseInt(variantEl.value, 10) : null;
        var qtyEl = form.querySelector('[name="quantity"]');
        var mainQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

        // Collect arlo-options + photo-uploader hidden property inputs
        var mainProperties = collectMainProperties();

        // Show ATC loading state so UX matches normal cart add
        var atcBtn = form.querySelector('[data-add-to-cart]');
        if (atcBtn) atcBtn.classList.add('loading');

        var mainBody = { id: mainVariantId, quantity: mainQty };
        if (Object.keys(mainProperties).length) mainBody.properties = mainProperties;

        var mainPromise = mainVariantId ? fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(mainBody),
        }).catch(function (err) {
          console.warn('[ArloOptions] Failed to add main product:', err);
        }) : Promise.resolve();

        Promise.all([addLinkedProducts(options), mainPromise]).then(function () {
          if (atcBtn) atcBtn.classList.remove('loading');
          // Refresh quick-cart HTML and open it via theme internal events
          if (window.emit) {
            window.emit('quick-cart:updated');
            window.emit('quick-cart:open');
          }
          // Fire DOM event to trigger recs refresh + reset personalisation
          document.dispatchEvent(new CustomEvent('cart:item-added'));
        }).catch(function (err) {
          if (atcBtn) atcBtn.classList.remove('loading');
          console.warn('[ArloOptions] cart add failed:', err);
        });

        return;
      }

      // No linked products, no canvas — use fetch so we submit ONLY our container
      // properties and avoid any spurious inputs the native form might include.
      e.preventDefault();
      e.stopImmediatePropagation();

      var variantEl = form.querySelector('[name="id"]');
      var mainVariantId = variantEl ? parseInt(variantEl.value, 10) : null;
      var qtyEl = form.querySelector('[name="quantity"]');
      var mainQty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;

      var mainProperties = collectMainProperties();

      var atcBtn2 = form.querySelector('[data-add-to-cart]');
      if (atcBtn2) atcBtn2.classList.add('loading');

      var mainBody2 = { id: mainVariantId, quantity: mainQty };
      if (Object.keys(mainProperties).length) mainBody2.properties = mainProperties;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mainBody2),
      }).then(function () {
        if (atcBtn2) atcBtn2.classList.remove('loading');
        if (window.emit) {
          window.emit('quick-cart:updated');
          window.emit('quick-cart:open');
        }
        document.dispatchEvent(new CustomEvent('cart:item-added'));
      }).catch(function (err) {
        if (atcBtn2) atcBtn2.classList.remove('loading');
        console.warn('[ArloOptions] cart add failed:', err);
      });
    }

    form.addEventListener('submit', function (e) { handleSubmit(e, false); }, true);

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      handleSubmit(e, true);
    }, true);
  }

  // ── Canvas PNG capture ────────────────────────────────────────────────────────
  var MM_PER_INCH = 25.4;

  function runCanvasCapture(options, form, canvasOptions, onComplete) {
    // onComplete: called once all artwork PNGs are generated and their hidden
    // property inputs are populated. Falls back to a native form submit if not
    // provided (kept for safety / backward-compat).
    var done = typeof onComplete === 'function' ? onComplete : function () { form.submit(); };
    var config = window.ArloProductConfig || {};
    var ucKey = config.uploadcareCanvasPngPublicKey || '';
    var fontMap = {};
    (config.fonts || []).forEach(function (f) { fontMap[f.id] = f; });

    var statusEl = getOrCreateStatusEl();
    showStatus(statusEl, 'Preparing your artwork…', 'loading');

    // Build a list of canvas render tasks
    var tasks = [];
    canvasOptions.forEach(function (opt) {
      var container = document.getElementById(CONTAINER_ID);
      var wrap = container ? container.querySelector('[data-opt-id="' + opt.id + '"]') : null;
      var fpEl = wrap ? wrap.querySelector('.arlo-opt-fp-input') : null;
      var fpH  = wrap ? wrap.querySelector('input[type="hidden"][name^="properties["]') : null;
      // Use innerText (not textContent) to preserve line breaks entered by the user.
      // textContent concatenates all block-element lines without any separator.
      var text = fpEl ? ((fpEl.innerText !== undefined ? fpEl.innerText : fpEl.textContent) || '').trim() : '';
      if (opt.uppercase) text = text.toUpperCase();
      if (!text || !fpH) return;

      // Resolve active font — check which font button is active if multiple fonts
      var activeFontId = null;
      if (wrap) {
        var activeBtn = wrap.querySelector('.arlo-opt-fp-font-btn.active');
        if (activeBtn) {
          // Find the font hidden input (stores displayName, not id) — use button index instead
          var allBtns = wrap.querySelectorAll('.arlo-opt-fp-font-btn');
          var btnIdx = Array.prototype.indexOf.call(allBtns, activeBtn);
          var fontIds = opt.fonts || [];
          if (btnIdx >= 0 && btnIdx < fontIds.length) activeFontId = fontIds[btnIdx];
        }
      }
      if (!activeFontId) activeFontId = (opt.fonts || [])[0];

      tasks.push({ text: text, fpH: fpH, fontId: activeFontId, cs: opt.canvasSettings || {} });
    });

    if (!tasks.length) { hideStatus(statusEl); done(); return; }

    (function runTasks(idx) {
      if (idx >= tasks.length) {
        hideStatus(statusEl);
        done();
        return;
      }
      var task = tasks[idx];
      var fontConfig = fontMap[task.fontId];

      showStatus(statusEl, 'Loading font…', 'loading');
      loadCanvasFont(fontConfig).then(function () {
        showStatus(statusEl, 'Generating artwork…', 'loading');
        var canvas = renderTextToCanvas(task.text, fontConfig, task.cs);

        if (ucKey) {
          showStatus(statusEl, 'Uploading artwork…', 'loading');
          canvasToBlob(canvas).then(function (blob) {
            var filename = 'artwork-' + Date.now() + '-' + idx + '.png';
            uploadToUploadcare(blob, ucKey, filename).then(function (url) {
              task.fpH.value = url;
              runTasks(idx + 1);
            }).catch(function (err) {
              console.error('[Arlo] Upload error:', err);
              // Proceed with raw text if upload fails
              runTasks(idx + 1);
            });
          });
        } else {
          // No UC key — embed as data URL (useful for testing)
          task.fpH.value = canvas.toDataURL('image/png');
          runTasks(idx + 1);
        }
      }).catch(function () {
        runTasks(idx + 1);
      });
    })(0);
  }

  function loadCanvasFont(fontConfig) {
    if (!fontConfig || !fontConfig.files || !fontConfig.files.length) {
      return Promise.resolve();
    }
    if (document.fonts && document.fonts.check('12px \'' + fontConfig.id + '\'')) {
      return Promise.resolve();
    }
    var files = fontConfig.files;
    var idx = 0;
    function tryNext() {
      if (idx >= files.length) return Promise.reject(new Error('No font file loaded'));
      var file = files[idx++];
      var face = new FontFace(fontConfig.id, 'url(\'' + file.url + '\')');
      return face.load().then(function (loaded) {
        document.fonts.add(loaded);
        return document.fonts.ready;
      }).catch(tryNext);
    }
    return tryNext();
  }

  // ♥ (U+2665) has no glyph in any of our uploaded monogram fonts, so the browser
  // silently falls back to a system font for just that character when it hits
  // ctx.fillText() — and on most platforms that fallback is a COLOR emoji font
  // (bitmap/COLR glyphs), which Canvas2D can NEVER recolor via ctx.fillStyle. No
  // amount of CSS (font-variant-emoji, variation selectors) fixes this, because
  // those only affect text layout/selection, not what Canvas2D actually paints.
  // Fix: never ask the font to render ♥ at all — draw it ourselves as a filled
  // vector path in the exact same fillStyle as the rest of the text, so it can
  // only ever come out as plain black (or whatever the monogram color is).
  var HEART_CHAR = '♥';

  function drawHeartGlyph(ctx, x, topY, size, color) {
    var w = size, h = size * 0.9;
    var topCurveHeight = h * 0.3;
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(x + w / 2, topY + topCurveHeight);
    ctx.bezierCurveTo(x + w / 2, topY, x, topY, x, topY + topCurveHeight);
    ctx.bezierCurveTo(x, topY + (h + topCurveHeight) / 2, x + w / 2, topY + (h + topCurveHeight) / 2, x + w / 2, topY + h);
    ctx.bezierCurveTo(x + w / 2, topY + (h + topCurveHeight) / 2, x + w, topY + (h + topCurveHeight) / 2, x + w, topY + topCurveHeight);
    ctx.bezierCurveTo(x + w, topY, x + w / 2, topY, x + w / 2, topY + topCurveHeight);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  // Renders one line starting at (startX, topY) with textAlign effectively 'left',
  // substituting a drawn heart shape for every ♥ instead of fillText-ing it.
  // Returns nothing — paints directly, matching ctx.fillText's own contract.
  function fillTextWithHeart(ctx, line, startX, topY, fs, color) {
    var heartSize = Math.round(fs * 0.74);
    var heartTopOffset = Math.round(fs * 0.06);
    var cx = startX;
    for (var i = 0; i < line.length; i++) {
      var ch = line[i];
      if (ch === HEART_CHAR) {
        drawHeartGlyph(ctx, cx, topY + heartTopOffset, heartSize, color);
        cx += heartSize;
      } else {
        ctx.fillText(ch, cx, topY);
        cx += ctx.measureText(ch).width;
      }
    }
  }

  // Same left-to-right walk as fillTextWithHeart, but just sums widths — used to
  // find the correct starting X for center/right alignment before painting.
  function measureLineWithHeart(ctx, line, fs) {
    var heartSize = Math.round(fs * 0.74);
    var w = 0;
    for (var i = 0; i < line.length; i++) {
      w += (line[i] === HEART_CHAR) ? heartSize : ctx.measureText(line[i]).width;
    }
    return w;
  }

  function renderTextToCanvas(text, fontConfig, cs) {
    cs = cs || {};
    var dpi  = cs.dpi  || 300;
    var wMm  = cs.templateWidthMm  || 150;
    var hMm  = cs.templateHeightMm || 50;
    var wPx  = Math.round((wMm / MM_PER_INCH) * dpi);
    var hPx  = Math.round((hMm / MM_PER_INCH) * dpi);
    // fontSize stored in pt; convert to px at the output DPI (1pt = dpi/72 px)
    var fsPt = cs.fontSize;
    var fs   = fsPt ? Math.round(fsPt * dpi / 72) : Math.round(hPx * 0.4);
    var color    = cs.color  || '#000000';
    var vAlign   = cs.vAlign || 'middle';
    var hAlign   = cs.hAlign || 'center';
    var vOffset  = cs.vOffset || 0;
    var kerning  = cs.kerning || 0;
    // Admin stores this as canvasSettings.leading (see admin/public/index.html
    // #p-canvas-leading) — cs.lineHeight was never actually the right key, so this
    // was silently ignoring the admin's Line Spacing setting and always using the
    // 1.25 fallback. Keep .lineHeight as a defensive fallback only.
    var lineHeightMult = cs.leading || cs.lineHeight || 1.25;
    var fontFamily = fontConfig ? '\'' + fontConfig.id + '\'' : 'sans-serif';

    var isCirclePng = cs.shape === 'circle';

    var canvas = document.createElement('canvas');
    canvas.width  = wPx;
    canvas.height = hPx;
    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, wPx, hPx);

    // Circle clip — mask to circle for circle artboards
    if (isCirclePng) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(wPx / 2, hPx / 2, Math.min(wPx, hPx) / 2, 0, Math.PI * 2);
      ctx.clip();
    }

    ctx.font      = fs + 'px ' + fontFamily;
    ctx.fillStyle = color;
    ctx.textAlign = hAlign === 'center' ? 'center' : hAlign;

    if (kerning) ctx.letterSpacing = kerning + 'px';

    // ── Word-wrap ────────────────────────────────────────────────────────────
    // The contenteditable div wraps text visually in the browser but innerText
    // only contains explicit \n characters typed by the user (Enter key).
    // We must replicate the wrapping here so long lines break at the canvas edge.
    //
    // Max usable width:
    //   rectangle → full canvas width minus one character of padding each side
    //   circle    → ~82% of diameter (inscribed rectangle that avoids clipped edges)
    var edgePad  = Math.round(fs * 0.5);
    // Circle: wrap text within the inscribed safe zone (~70% of diameter)
    // matching the 15% inset applied to the storefront input box.
    var maxWidth = isCirclePng ? Math.round(wPx * 0.70) : wPx - edgePad * 2;

    // Split on explicit newlines first, then word-wrap each segment
    var hardLines = text.split('\n');
    var lines = [];
    hardLines.forEach(function (hard) {
      if (!hard) { lines.push(''); return; } // preserve intentional blank lines
      var words = hard.split(' ');
      var cur = '';
      words.forEach(function (word) {
        var test = cur ? cur + ' ' + word : word;
        if (ctx.measureText(test).width > maxWidth && cur) {
          lines.push(cur);
          cur = word;
        } else {
          cur = test;
        }
      });
      if (cur) lines.push(cur);
    });

    var lineH  = Math.round(fs * lineHeightMult);
    var totalH = lines.length * lineH;

    // Horizontal anchor
    var x = hAlign === 'center' ? wPx / 2
          : hAlign === 'left'   ? edgePad
          : wPx - edgePad;

    // Vertical start: centre the whole block, then apply vOffset
    var blockTop;
    if (vAlign === 'top') {
      blockTop = edgePad;
    } else if (vAlign === 'bottom') {
      blockTop = hPx - totalH - edgePad;
    } else {
      blockTop = Math.round((hPx - totalH) / 2);
    }
    blockTop += vOffset;

    ctx.textBaseline = 'top';
    lines.forEach(function (line, i) {
      var y = blockTop + i * lineH;
      if (line.indexOf(HEART_CHAR) === -1) {
        ctx.fillText(line, x, y);
        return;
      }
      // Line contains a ♥ — paint it manually left-to-right (see
      // fillTextWithHeart above) so the heart is drawn as a shape, not routed
      // through font fallback. ctx.textAlign doesn't apply to manual per-
      // character fillText calls, so work out the correct starting X first,
      // matching whatever alignment the rest of this render is using.
      var lineWidth = measureLineWithHeart(ctx, line, fs);
      var startX = hAlign === 'center' ? x - lineWidth / 2
                 : hAlign === 'left'   ? x
                 : x - lineWidth; // right-aligned: x is the right edge
      var prevAlign = ctx.textAlign;
      ctx.textAlign = 'left';
      fillTextWithHeart(ctx, line, startX, y, fs, color);
      ctx.textAlign = prevAlign;
    });

    if (isCirclePng) ctx.restore();

    return canvas;
  }

  // ── PNG Preview overlay ────────────────────────────────────────────────────
  function showPngPreview(canvas, dpi) {
    // Remove any existing overlay
    var old = document.getElementById('arlo-png-preview-overlay');
    if (old) old.remove();

    var wPx = canvas.width;
    var hPx = canvas.height;
    var MM_PER_INCH = 25.4;
    var wMm = Math.round((wPx / dpi) * MM_PER_INCH);
    var hMm = Math.round((hPx / dpi) * MM_PER_INCH);

    var overlay = document.createElement('div');
    overlay.id = 'arlo-png-preview-overlay';
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:99999',
      'background:rgba(0,0,0,0.85)',
      'display:flex', 'flex-direction:column',
      'align-items:center', 'justify-content:center',
      'padding:24px', 'box-sizing:border-box'
    ].join(';');

    // Checkerboard background behind image (shows transparency)
    var imgWrap = document.createElement('div');
    imgWrap.style.cssText = [
      'background: repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 0 / 16px 16px',
      'max-width:min(90vw,600px)', 'max-height:60vh',
      'display:flex', 'align-items:center', 'justify-content:center',
      'overflow:hidden', 'border-radius:4px'
    ].join(';');

    var img = document.createElement('img');
    img.src = canvas.toDataURL('image/png');
    img.style.cssText = 'max-width:100%;max-height:60vh;display:block;';
    imgWrap.appendChild(img);

    // Info bar
    var info = document.createElement('div');
    info.style.cssText = 'color:#fff;font-size:13px;margin-top:12px;text-align:center;font-family:-apple-system,sans-serif;opacity:0.8;';
    info.textContent = wPx.toLocaleString() + ' × ' + hPx.toLocaleString() + ' px  ·  ' + wMm + ' × ' + hMm + 'mm  ·  ' + dpi + ' dpi';

    // Buttons row
    var btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:10px;margin-top:16px;';

    var dlBtn = document.createElement('a');
    dlBtn.download = 'preview-' + Date.now() + '.png';
    dlBtn.href = canvas.toDataURL('image/png');
    dlBtn.textContent = 'Download PNG';
    dlBtn.style.cssText = [
      'padding:8px 20px', 'background:#fff', 'color:#111',
      'border-radius:4px', 'font-size:13px', 'font-weight:600',
      'text-decoration:none', 'font-family:-apple-system,sans-serif'
    ].join(';');

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.textContent = 'Close';
    closeBtn.style.cssText = [
      'padding:8px 20px', 'background:transparent', 'color:#fff',
      'border:1px solid rgba(255,255,255,0.4)', 'border-radius:4px',
      'font-size:13px', 'font-weight:600', 'cursor:pointer',
      'font-family:-apple-system,sans-serif'
    ].join(';');
    closeBtn.addEventListener('click', function () { overlay.remove(); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) overlay.remove(); });

    btnRow.appendChild(dlBtn);
    btnRow.appendChild(closeBtn);
    overlay.appendChild(imgWrap);
    overlay.appendChild(info);
    overlay.appendChild(btnRow);
    document.body.appendChild(overlay);
  }

  function canvasToBlob(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob); else reject(new Error('toBlob failed'));
      }, 'image/png');
    });
  }

  function uploadToUploadcare(blob, publicKey, filename) {
    var fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', publicKey);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', blob, filename || 'artwork.png');
    return fetch('https://upload.uploadcare.com/base/', { method: 'POST', body: fd })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data.file) throw new Error('No file UUID');
        return 'https://ucarecdn.com/' + data.file + '/';
      });
  }

  function getOrCreateStatusEl() {
    var el = document.getElementById('arlo-canvas-status');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'arlo-canvas-status';
    el.style.cssText = 'display:none;position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:#1a1a1a;color:#fff;padding:12px 24px;border-radius:8px;font-size:14px;font-family:-apple-system,sans-serif;z-index:9999;box-shadow:0 4px 20px rgba(0,0,0,0.3);align-items:center;gap:10px;max-width:90vw';
    document.body.appendChild(el);
    return el;
  }

  function showStatus(el, msg, type) {
    el.style.display = 'flex';
    el.innerHTML = (type === 'loading'
      ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" style="animation:arlo-spin 0.8s linear infinite;flex-shrink:0"><circle cx="12" cy="12" r="10" opacity="0.3"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>'
      : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>')
      + '<span>' + msg + '</span>';
    if (!document.getElementById('arlo-spin-style')) {
      var s = document.createElement('style');
      s.id = 'arlo-spin-style';
      s.textContent = '@keyframes arlo-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(s);
    }
  }

  function hideStatus(el) { if (el) el.style.display = 'none'; }

  // ── Get current product handle ────────────────────────────────────────────────
  function getCurrentHandle() {
    var handle = (window.ShopifyAnalytics &&
                  window.ShopifyAnalytics.meta &&
                  window.ShopifyAnalytics.meta.product &&
                  window.ShopifyAnalytics.meta.product.handle) || null;
    if (!handle) {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      handle = m ? m[1] : null;
    }
    return handle;
  }

  // ── Init ─────────────────────────────────────────────────────────────────────
  function init() {
    var handle = getCurrentHandle();
    var config = window.ArloProductConfig || {};

    // Only run on products configured in the Arlo admin
    var isArloProduct = handle && (
      (config.products && config.products[handle]) ||
      (config.productOptions && config.productOptions[handle]) ||
      (config.uploadcareVariants && config.uploadcareVariants[handle]) ||
      (config.variantButtons && config.variantButtons[handle])
    );
    if (!isArloProduct) {
      // Remove the class so any scoped suppress CSS stops applying immediately.
      // Critical for AJAX navigation: <head> style element persists across pages.
      document.documentElement.classList.remove('arlo-managed');
      return;
    }

    suppressNativeOptions();

    var options = getOptions();
    if (!options || !options.length) return;

    var form = document.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    injectStyles();
    buildFields(options, form);
    hookForm(options, form);
  }

  function waitForConfig(cb, attempts) {
    attempts = attempts || 0;
    if (window.ArloProductConfig) return cb();
    if (attempts > 30) return;
    setTimeout(function () { waitForConfig(cb, attempts + 1); }, 100);
  }

  // ── Reset all personalisation after successful cart add ───────────────────────
  function resetPersonalisation() {
    var container = document.getElementById(CONTAINER_ID);
    if (!container) return;

    // Clear all hidden property inputs
    container.querySelectorAll('input[type="hidden"]').forEach(function (el) {
      el.value = '';
    });

    // Deselect buttons and tiles
    container.querySelectorAll('.arlo-opt-button, .arlo-opt-tile').forEach(function (el) {
      el.classList.remove('active');
    });

    // Clear text inputs and contenteditable (monogram, font-previewer)
    container.querySelectorAll('input[type="text"], input:not([type])').forEach(function (el) {
      el.value = '';
      el.dispatchEvent(new Event('input'));
    });
    container.querySelectorAll('[contenteditable]').forEach(function (el) {
      el.textContent = '';
      el.dispatchEvent(new Event('input'));
    });

    // Reset selects to first option
    container.querySelectorAll('select').forEach(function (el) {
      el.selectedIndex = 0;
      el.dispatchEvent(new Event('change'));
    });

    // Uncheck checkboxes
    container.querySelectorAll('input[type="checkbox"]').forEach(function (el) {
      el.checked = false;
    });

    // Remove error states
    container.querySelectorAll('.arlo-opt-error').forEach(function (el) {
      el.classList.remove('arlo-opt-error');
    });
    container.querySelectorAll('.arlo-opt-err').forEach(function (el) {
      el.style.display = 'none';
    });

    // Reset linked price delta
    if (_linkedExtraCents !== 0) {
      applyLinkedPriceDelta(-_linkedExtraCents);
    }

    // Reset photo uploader
    if (window.ArloPhotoUploader) {
      window.ArloPhotoUploader.reset();
    }
  }

  document.addEventListener('cart:item-added', resetPersonalisation);

  document.addEventListener('DOMContentLoaded', function () {
    waitForConfig(init);
  });

})();
